import React, { useEffect, useRef, useState } from 'react';
import ScoreBoard from './ScoreBoard';

// ─── Particle explosion ────────────────────────────────────────────────────
const PARTICLE_COUNT = 28;
const COLORS = ['#d4a843', '#f2cc70', '#27ae60', '#e74c3c', '#9b59b6', '#f5ead2'];

function ParticleBurst({ active }) {
  const particles = useRef(
    Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const angle = (i / PARTICLE_COUNT) * 360;
      const dist  = 80 + Math.random() * 120;
      const rad   = (angle * Math.PI) / 180;
      return {
        id:    i,
        color: COLORS[i % COLORS.length],
        shape: Math.random() > 0.5 ? 'circle' : 'rect',
        dx:    `${Math.cos(rad) * dist}px`,
        dy:    `${Math.sin(rad) * dist}px`,
        dr:    `${-180 + Math.random() * 360}deg`,
        dur:   `${0.6 + Math.random() * 0.8}s`,
        size:  4 + Math.random() * 6,
      };
    })
  ).current;

  if (!active) return null;

  return (
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      width: 0, height: 0, pointerEvents: 'none', zIndex: 10,
    }}>
      {particles.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            position: 'absolute',
            width:  `${p.size}px`,
            height: p.shape === 'rect' ? `${p.size * 0.4}px` : `${p.size}px`,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            background: p.color,
            top:  `-${p.size / 2}px`,
            left: `-${p.size / 2}px`,
            '--dx':  p.dx,
            '--dy':  p.dy,
            '--dr':  p.dr,
            '--dur': p.dur,
          }}
        />
      ))}
    </div>
  );
}

// ─── Shockwave ring ───────────────────────────────────────────────────────
function ShockwaveRing({ active, color = 'rgba(212,168,67,0.7)' }) {
  if (!active) return null;
  return (
    <div style={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none', zIndex: 9,
    }}>
      <div
        className="shockwave-ring"
        style={{
          width: '80px', height: '80px',
          borderRadius: '50%',
          border: `3px solid ${color}`,
          boxShadow: `0 0 20px ${color}`,
        }}
      />
    </div>
  );
}

// ─── Main RoundEndScreen ──────────────────────────────────────────────────
export function RoundEndScreen({ game, playerId, room, onStartNewRound, onLeaveRoom }) {
  const [stage, setStage] = useState(0); // 0=hidden, 1=burst, 2=title, 3=content
  const isHost         = room?.hostId === playerId;
  const isDeckExhausted = game?.lastAction?.deckExhausted;
  const roundWinner    = isDeckExhausted
    ? null
    : game?.players?.find(p => p.id === game?.lastAction?.roundWinnerId);
  const isWinnerYou    = roundWinner?.id === playerId;

  useEffect(() => {
    // Stage machine: burst → title → content
    const t1 = setTimeout(() => setStage(1), 50);
    const t2 = setTimeout(() => setStage(2), 200);
    const t3 = setTimeout(() => setStage(3), 400);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, []);

  const glowColor  = isDeckExhausted
    ? 'rgba(230,126,34,0.6)'
    : 'rgba(212,168,67,0.6)';

  return (
    <div
      className="round-end-overlay min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 50% 30%, rgba(30,60,30,0.9) 0%, rgba(13,31,20,0.98) 70%)',
      }}
    >
      {/* Background decorative rings */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
      }}>
        {[200, 350, 500].map((r, i) => (
          <div key={i} style={{
            position: 'absolute',
            top: '30%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${r}px`, height: `${r}px`,
            borderRadius: '50%',
            border: '1px solid rgba(212,168,67,0.06)',
          }} />
        ))}
      </div>

      {/* Particle burst origin */}
      <div style={{ position: 'absolute', top: '28%', left: '50%' }}>
        <ShockwaveRing active={stage >= 1} color={glowColor} />
        <ParticleBurst active={stage >= 1} />
      </div>

      {/* Title block */}
      <div className={`text-center mb-8 relative z-20 ${stage >= 2 ? 'round-end-title' : 'opacity-0'}`}>

        {/* Deck exhausted variant */}
        {isDeckExhausted ? (
          <>
            <div style={{
              fontFamily: '"IM Fell English SC", Georgia, serif',
              fontSize: '0.85rem', letterSpacing: '0.25em',
              color: 'rgba(230,126,34,0.7)',
              marginBottom: '8px',
              textTransform: 'uppercase',
            }}>
              — deck exhausted —
            </div>
            <h1 style={{
              fontFamily: '"Cinzel", Georgia, serif',
              fontSize: 'clamp(2rem, 6vw, 3.2rem)',
              fontWeight: '900',
              letterSpacing: '0.06em',
              color: '#e67e22',
              animation: 'titleGlow 2.5s ease-in-out infinite',
              marginBottom: '12px',
            }}>
              牌堆已尽
            </h1>
            <p style={{
              fontFamily: '"IM Fell English SC", Georgia, serif',
              fontSize: '0.95rem', color: 'rgba(245,234,210,0.7)',
              letterSpacing: '0.05em',
            }}>
              All remaining cards are scored.
            </p>
          </>
        ) : (
          <>
            {/* Round number tag */}
            <div style={{
              fontFamily: '"IM Fell English SC", Georgia, serif',
              fontSize: '0.8rem', letterSpacing: '0.2em',
              color: 'rgba(212,168,67,0.55)',
              marginBottom: '6px',
            }}>
              ◆◆◆ &nbsp; Round {game.roundNumber} &nbsp; ◆◆◆
            </div>

            {/* Main title */}
            <h1 style={{
              fontFamily: '"Cinzel", Georgia, serif',
              fontSize: 'clamp(2.2rem, 7vw, 3.8rem)',
              fontWeight: '900',
              letterSpacing: '0.06em',
              // Gold shimmer gradient text
              background: 'linear-gradient(90deg, #a07820 0%, #f2cc70 30%, #d4a843 50%, #f2cc70 70%, #a07820 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'titleShimmer 3s linear infinite, titleGlow 2s ease-in-out infinite',
              marginBottom: '10px',
              lineHeight: 1.1,
            }}>
              Complete!
            </h1>
          </>
        )}

        {/* Winner announcement */}
        {roundWinner && (
          <div className="round-end-subtitle" style={{ marginTop: '10px' }}>
            <span style={{
              fontFamily: '"Philosopher", Georgia, serif',
              fontSize: '1.05rem',
              color: isWinnerYou ? '#27ae60' : 'rgba(245,234,210,0.75)',
            }}>
              {isWinnerYou ? '🎉 You went out first!' : `${roundWinner.name} went out first!`}
            </span>
          </div>
        )}
      </div>

      {/* Scores + action — staged reveal */}
      <div className={stage >= 3 ? 'round-end-content' : 'opacity-0'} style={{ width: '100%', maxWidth: '480px', zIndex: 20 }}>
        <ScoreBoard players={game.players} playerId={playerId} />
      </div>

      <div className={stage >= 3 ? 'round-end-action' : 'opacity-0'} style={{ marginTop: '24px', zIndex: 20 }}>
        {isHost ? (
          <button
            onClick={onStartNewRound}
            className="btn-primary"
            style={{ fontSize: '0.9rem', letterSpacing: '0.12em' }}
          >
            ▶ Start Next Round
          </button>
        ) : (
          <>
            <p style={{
              fontFamily: '"IM Fell English SC", Georgia, serif',
              fontSize: '0.8rem', letterSpacing: '0.08em',
              color: 'rgba(245,234,210,0.4)',
              textAlign: 'center',
            }}>
              Waiting for host to start next round…
            </p>
            <button
              onClick={onLeaveRoom}
              className="btn-secondary"
              style={{ marginTop: '12px', fontSize: '0.8rem' }}
            >
              Leave Game
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── GameEndScreen ─────────────────────────────────────────────────────────
export function GameEndScreen({ game, playerId, room, onRematch, onLeaveRoom }) {
  const [stage, setStage] = useState(0);
  const isHost   = room?.hostId === playerId;
  const winner   = game?.players?.find(p => p.id === game?.winner);
  const isWinner = game?.winner === playerId;

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 50);
    const t2 = setTimeout(() => setStage(2), 150);
    const t3 = setTimeout(() => setStage(3), 380);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, []);

  return (
    <div
      className="round-end-overlay min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: isWinner
          ? 'radial-gradient(ellipse at 50% 25%, rgba(39,100,50,0.95) 0%, rgba(13,31,20,0.99) 65%)'
          : 'radial-gradient(ellipse at 50% 25%, rgba(25,25,50,0.95) 0%, rgba(13,20,30,0.99) 65%)',
      }}
    >
      {/* Deco rings */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {[180, 320, 480].map((r, i) => (
          <div key={i} style={{
            position: 'absolute', top: '28%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${r}px`, height: `${r}px`,
            borderRadius: '50%',
            border: `1px solid ${isWinner ? 'rgba(212,168,67,0.08)' : 'rgba(255,255,255,0.04)'}`,
          }} />
        ))}
      </div>

      {/* Burst */}
      <div style={{ position: 'absolute', top: '26%', left: '50%' }}>
        <ShockwaveRing active={stage >= 1} color={isWinner ? 'rgba(212,168,67,0.8)' : 'rgba(100,120,255,0.6)'} />
        {stage >= 1 && <ParticleBurst active />}
      </div>

      {/* Title */}
      <div className={`text-center mb-8 relative z-20 ${stage >= 2 ? 'round-end-title' : 'opacity-0'}`}>
        <div style={{
          fontFamily: '"IM Fell English SC", Georgia, serif',
          fontSize: '0.8rem', letterSpacing: '0.22em',
          color: isWinner ? 'rgba(212,168,67,0.6)' : 'rgba(255,255,255,0.3)',
          marginBottom: '8px',
        }}>
          ◆◆◆ &nbsp; Final Result &nbsp; ◆◆◆
        </div>

        <h1 style={{
          fontFamily: '"Cinzel", Georgia, serif',
          fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
          fontWeight: '900',
          letterSpacing: '0.04em',
          background: isWinner
            ? 'linear-gradient(90deg, #a07820 0%, #f2cc70 35%, #d4a843 50%, #f2cc70 65%, #a07820 100%)'
            : 'linear-gradient(90deg, #c0c0c0 0%, #ffffff 40%, #c0c0c0 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: 'titleShimmer 2.5s linear infinite',
          lineHeight: 1.05,
          marginBottom: '8px',
        }}>
          {isWinner ? 'Victory!' : 'Game Over'}
        </h1>

        <div className="round-end-subtitle" style={{ marginTop: '8px' }}>
          <span style={{
            fontFamily: '"Philosopher", Georgia, serif',
            fontSize: '1.05rem',
            color: isWinner ? '#27ae60' : 'rgba(245,234,210,0.65)',
          }}>
            {isWinner ? '🏆 You won the entire game!' : `${winner?.name} wins the game!`}
          </span>
        </div>
      </div>

      {/* Scoreboard */}
      <div className={stage >= 3 ? 'round-end-content' : 'opacity-0'} style={{ width: '100%', maxWidth: '480px', zIndex: 20 }}>
        <ScoreBoard players={game.players} playerId={playerId} final />
      </div>

      {/* Buttons */}
      <div className={stage >= 3 ? 'round-end-action' : 'opacity-0'} style={{ display: 'flex', gap: '12px', marginTop: '24px', zIndex: 20 }}>
        {isHost && onRematch && (
          <button onClick={onRematch} className="btn-primary" style={{ fontSize: '0.85rem', letterSpacing: '0.1em' }}>
            ↺ Play Again
          </button>
        )}
        <button onClick={onLeaveRoom} className="btn-secondary" style={{ fontSize: '0.85rem' }}>
          Leave Game
        </button>
      </div>
      {!isHost && (
        <p className="round-end-action" style={{
          fontFamily: '"IM Fell English SC", Georgia, serif',
          fontSize: '0.75rem', color: 'rgba(245,234,210,0.35)',
          letterSpacing: '0.06em', marginTop: '12px', zIndex: 20,
        }}>
          Waiting for host to start rematch…
        </p>
      )}
    </div>
  );
}
